import React from "react";
import logo from "assets/contract/logo.png";
import cover from "assets/contract/cover.png";
import wm1 from "assets/contract/watermark1.png";
import wm2 from "assets/contract/watermark2.png";
import wm3 from "assets/contract/watermark3.png";
import wm4 from "assets/contract/watermark4.png";
import wm5 from "assets/contract/watermark5.png";
import wm6 from "assets/contract/watermark6.png";

// the Word file has faint page watermarks behind its text: repeated down the sheet here
const WATERMARKS = [wm1, wm2, wm3, wm4, wm5, wm6];
const watermarkStyle = {
  backgroundImage: WATERMARKS.map((w) => `url(${w})`).join(", "),
  backgroundPosition: WATERMARKS.map((_, i) => `center ${1500 + i * 2100}px`).join(", "),
  backgroundSize: "62% auto",
  backgroundRepeat: "no-repeat",
};

// "______" fill-in blanks become inline lines (a raw run of underscores cannot wrap and
// stretches justified text)
const withBlanks = (text) =>
  text.split(/(_{4,})/).map((part, i) =>
    /^_{4,}$/.test(part) ? (
      <span key={i} className="ct_blank" style={{ minWidth: `${Math.min(part.length, 28) * 0.55}em` }} />
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );

// bold / normal runs of a paragraph
const Segs = ({ segs }) =>
  segs.map((s, i) => (s.b ? <strong key={i}>{withBlanks(s.t)}</strong> : <React.Fragment key={i}>{withBlanks(s.t)}</React.Fragment>));

const plain = (p) => p.segs.map((s) => s.t).join("");

const Paragraph = ({ p, index }) => {
  const cls = ["ct_p"];
  if (p.kind === "clause") cls.push("ct_clause");
  if (p.kind === "item") cls.push("ct_item");
  if (p.kind === "recital") cls.push("ct_clause");
  if (p.align === "center") cls.push("ct_center");
  if (index === 0) cls.push("ct_title");
  if (index === 1) cls.push("ct_sub");
  if (index === 2) cls.push("ct_company");
  if (plain(p).trim() === "CONFIDENTIAL") cls.push("ct_confidential");
  return (
    <p className={cls.join(" ")}>
      <Segs segs={p.segs} />
    </p>
  );
};

const Cell = ({ paras }) => (
  <>
    {paras.map((p, i) => (
      <div key={i} className="ct_line">
        <Segs segs={p.segs} />
      </div>
    ))}
  </>
);

// the template's tables: particulars, callout boxes, notices and signatures
const Table = ({ rows }) => {
  const cols = Math.max(...rows.map((r) => r.length));
  if (rows.length === 1 && rows[0].length === 1) {
    const [head, ...rest] = rows[0][0];
    return (
      <div className="ct_callout">
        <strong className="ct_callout_title">
          <Segs segs={head.segs} />
        </strong>
        {rest.map((p, i) => (
          <p key={i}>
            <Segs segs={p.segs} />
          </p>
        ))}
      </div>
    );
  }
  if (rows.length === 1 && rows[0].length === 2) {
    // signature blocks
    return (
      <div className="ct_signatures">
        {rows[0].map((cell, i) => (
          <div key={i} className="ct_sign_box">
            <Cell paras={cell} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <table className="ct_table">
      <tbody>
        {rows.map((row, i) =>
          row.length === 1 && cols > 1 ? (
            <tr key={i} className="ct_table_head">
              <th colSpan={cols}>
                <Cell paras={row[0]} />
              </th>
            </tr>
          ) : (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className={i === 0 ? "ct_cell_head" : j === 0 && cols === 2 ? "ct_cell_label" : ""}>
                  <Cell paras={cell} />
                </td>
              ))}
            </tr>
          )
        )}
      </tbody>
    </table>
  );
};

const Annexure = ({ annexure }) => (
  <div className="ct_annexure">
    <h4 className="ct_heading">{annexure.title}</h4>
    {annexure.matrices.map((m) => (
      <div key={m.title} className="ct_matrix_block">
        <p className="ct_sub_title">{m.title}</p>
        <table className="ct_table ct_matrix">
          <thead>
            <tr>
              <th>Origin \ Destination</th>
              {m.zones.map((z) => (
                <th key={z}>{z}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {m.rows.map((row, i) => (
              <tr key={i}>
                <th>{m.zones[i]}</th>
                {row.map((v, j) => (
                  <td key={j}>{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ))}
    {(annexure.sections || []).map((s) => (
      <div key={s.title} className="ct_matrix_block">
        <p className="ct_sub_title">{s.title}</p>
        <table className="ct_table">
          <tbody>
            {s.rows.map((r, i) => (
              <tr key={i}>
                <td className="ct_cell_label">{r.label}</td>
                <td>{r.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ))}
  </div>
);

// The agreement as a document: letterhead, the template's clauses, then Annexure A and B
const ContractDocument = ({ contract }) => {
  let paraIndex = -1;
  return (
    <div className="ct_doc" style={watermarkStyle}>
      <div className="ct_logo">
        <img src={logo} alt="MV Load" />
      </div>

      {contract.blocks.map((b, i) => {
        if (b.type === "heading") return <h4 key={i} className="ct_heading">{b.text}</h4>;
        if (b.type === "table") {
          const isParticulars = plain(b.rows[0][0][0]).trim() === "AGREEMENT PARTICULARS";
          return (
            <React.Fragment key={i}>
              <Table rows={b.rows} />
              {isParticulars && <img className="ct_cover" src={cover} alt="" />}
            </React.Fragment>
          );
        }
        paraIndex += 1;
        return <Paragraph key={i} p={b} index={paraIndex} />;
      })}

      {contract.annexures && (
        <>
          <div className="ct_page_break" />
          <Annexure annexure={contract.annexures.A} />
          {contract.annexures.B.matrices.length > 0 && <Annexure annexure={contract.annexures.B} />}
        </>
      )}

      <div className="ct_footer">This contract has been digitally signed by the customer using OTP-based verification.</div>
    </div>
  );
};

export default ContractDocument;
